// traceability: requirements: [FR-002]
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '@/i18n';
import { queryClient } from '@/lib/queryClient';
import type { AuthTokens, User } from '@/types/models';

const SUPPORTED_LANGS = new Set(['en', 'ru', 'he']);

function syncUiLanguage(user: User | null) {
  if (!user?.language) return;
  const lang = user.language.split('-')[0];
  if (SUPPORTED_LANGS.has(lang) && i18n.language !== lang) {
    void i18n.changeLanguage(lang);
  }
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaRequired: boolean;
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  setMfaRequired: (required: boolean) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  updateUser: (partial: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,
      mfaRequired: false,
      setUser: (user) => {
        syncUiLanguage(user);
        set({ user, isAuthenticated: true });
      },
      setTokens: (tokens) => set({ tokens }),
      setMfaRequired: (mfaRequired) => set({ mfaRequired }),
      login: (user, tokens) => {
        queryClient.clear();
        syncUiLanguage(user);
        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          mfaRequired: false,
        });
      },
      logout: () => {
        queryClient.clear();
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          mfaRequired: false,
        });
      },
      updateUser: (partial) =>
        set((state) => {
          const updated = state.user ? { ...state.user, ...partial } : null;
          if (partial.language) syncUiLanguage(updated);
          return { user: updated };
        }),
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'tcm-auth',
      partialize: (state) => ({
        tokens: state.tokens,
      }),
    },
  ),
);
